import React, { useState, useRef, useEffect, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./styles/Navbar.css";
import { useAuth } from "../providers/AuthProvider";
import {
  FaCode,
  FaCheckCircle,
  FaTable,
  FaFlag,
  FaBalanceScale,
  FaChartBar,
  FaTachometerAlt,
  FaHome,
  FaPencilRuler,
  FaInfoCircle,
} from "react-icons/fa";
import ProjectDetails from "../components/ProjectDetails";
import VariableContext from "../contexts/VariableContext";

function Navbar() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null);
  const [allowedFeatures, setAllowedFeatures] = useState([]);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);

  const {
    setOriginalDataRows,
    selectedProjectDetails,
    setSelectedProjectDetails,
    loading,
    setLoading,
    setBanners,
    setdataGroup,
    dataGroup,
    projectId,
    setProjectId,
    setFrequencyData,
    setWeightingStats,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    setVariables,
    setSelectedClient,
    setSelectedProject,
    selectedProject,
    selectedClient,
    setSdataGroup,
    powner,
    setPowner,
    setSurveyFileName,
    setSurveyName,
  } = useContext(VariableContext);

  const dropdownRef = useRef(null);

  const hideLinksOnPaths = ["/home", "/login"];

  // Define all available navigation items with their feature keys
  const navItems = [
    {
      path: "/home",
      label: "Home",
      feature: "home",
      icon: <FaHome />,
      forceDisabled: false,
    },
    {
      path: "/surveybuilder",
      label: "Scripting",
      feature: "scripting",
      icon: <FaCode />,
      forceDisabled: false,
    },
    {
      path: "/datacheck",
      label: "Data Validate",
      feature: "datacheck",
      icon: <FaCheckCircle />,
      forceDisabled: false,
    },
    {
      path: "/datatables",
      label: "Data Tables",
      feature: "datatables",
      icon: <FaTable />,
      forceDisabled: false,
    },
    {
      path: "/banner",
      label: "Banner",
      feature: "banner",
      icon: <FaFlag />,
      forceDisabled: false,
    },
    {
      path: "/weighting",
      label: "Weighting",
      feature: "weighting",
      icon: <FaBalanceScale />,
      forceDisabled: true,
    },
    {
      path: "/dataanalysis",
      label: "Data Analysis",
      feature: "dataanalysis",
      icon: <FaChartBar />,
      forceDisabled: true,
    },
    {
      path: "/dashboardbuilder",
      label: "Dashboard",
      feature: "dashboard",
      icon: <FaTachometerAlt />,
      forceDisabled: false,
    },
    {
      path: "/qnrdesign",
      label: "Qnr Design",
      feature: "qnrdesign",
      icon: <FaPencilRuler />,
      badge: "(In Development)",
      forceDisabled: false,
    },
    {
      path: "/crosstabmodel",
      label: "Cross Tab",
      feature: "crosstab",
      icon: <FaTable />,
      forceDisabled: false,
    },
    {
      path: "/data",
      label: "Shopee",
      feature: "crosstab",
      icon: <FaTable />,
      forceDisabled: false,
    },
  ];

  const getInitial = (email) => {
    return email ? email.charAt(0).toUpperCase() : "U";
  };

  const getFullNameFromEmail = (email) => {
    if (!email) return "";
    const namePart = email.split("@")[0];
    const nameParts = namePart.split(/[._]/);
    const capitalizedParts = nameParts.map(
      (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    );
    return capitalizedParts.join(" ");
  };

  const userEmail = user?.user?.email || user?.email || "";
  const userName = getFullNameFromEmail(userEmail);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;

    if (organization) {
      if (organization.logo_url) {
        setOrgLogo(organization.logo_url);
      }

      if (
        organization.features &&
        Array.isArray(organization.features) &&
        organization.features.length > 0
      ) {
        setAllowedFeatures(organization.features);
      } else {
        // If no features defined, allow all by default
        setAllowedFeatures(navItems.map((item) => item.feature));
      }
    } else {
      console.warn("⚠️ No organization found, allowing all features");
      setAllowedFeatures(navItems.map((item) => item.feature));
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Check if a feature is allowed for the organization
  const hasFeatureAccess = (feature) => {
    return allowedFeatures.includes(feature);
  };

  // Function to close the modal
  const closeProjectDetailsModal = () => {
    setShowProjectDetailsModal(false);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <img
            src={
              orgLogo ||
              "https://excavateresearch.com/static/media/logo-small.89d00b404bbe8926034f.png"
            }
            alt="Organization Logo"
            className="logo"
          />
          <div className="nav-links">
            {!hideLinksOnPaths.includes(location.pathname) && (
              <>
                {navItems.map((item) => {
                  const hasAccess = hasFeatureAccess(item.feature);
                  const isEnabled = hasAccess && !item.forceDisabled;

                  // Don't show if user doesn't have access (unless forceDisabled is true, meaning we want to show it disabled)
                  if (!hasAccess && !item.forceDisabled) return null;

                  // Common content
                  const content = (
                    <>
                      <div className="nav-icon">{item.icon}</div>
                      <span className="nav-label">{item.label}</span>
                      {item.badge && (
                        <small className="beta-tag">{item.badge}</small>
                      )}
                      
                    </>
                  );

                  // For disabled items, use a span instead of NavLink
                  if (!isEnabled) {
                    return (
                      <span
                        key={item.path}
                        className="nav-item nav-item-disabled"
                        title="This feature is currently disabled"
                      >
                        {content}
                      </span>
                    );
                  }

                  // For enabled items, use NavLink as normal
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className="nav-item"
                    >
                      {content}
                    </NavLink>
                  );
                })}
              </>
            )}
          </div>
          <div className="profile-section" ref={dropdownRef}>
            {/* View Project Details Button */}
            <button
              className="project-details-btn"
              onClick={() => setShowProjectDetailsModal(true)}
              title="View Project Details"
            >
              <FaInfoCircle />
            </button>

            <div
              className="profile-avatar"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              {getInitial(userEmail)}
            </div>

            {showDropdown && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">{getInitial(userEmail)}</div>
                  <div className="dropdown-info">
                    <div className="dropdown-name">{userName}</div>
                    <div className="dropdown-email">{userEmail}</div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <button className="dropdown-logout" onClick={logout}>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Project Details Modal */}
      {showProjectDetailsModal && (
        <div className="modal-overlay" onClick={closeProjectDetailsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Project Details</h2>
              <button
                className="modal-close-btn"
                onClick={closeProjectDetailsModal}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <ProjectDetails
                project={selectedProjectDetails}
                projectid={projectId}
                projectOwner={powner}
              />
            </div>
            <div className="modal-footer">
              <button
                className="modal-close-button"
                onClick={closeProjectDetailsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
